import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  IconButton, 
  Paper, 
  ImageList, 
  ImageListItem, 
  ImageListItemBar,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Home as HomeIcon, 
  ChevronRight, 
  PermMedia as MediaIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from './Dexie';
import { syncMedia, addMedia, deleteMedia } from '../services/productService';

function TechyShopMedia() {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  
  // State for Add Media Modal
  const [openAddModal, setOpenAddModal] = useState(false);
  const [newMedia, setNewMedia] = useState({
    tabella: 'techyshop_prodotti',
    element_id: '',
    alt_text: '',
    ordinamento: 0,
    principale: 0,
    file: null,
    filePreview: null
  });

  const mediaItems = useLiveQuery(
    () => db.techyshop_medias.toArray()
  );

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  const handleSyncMedia = async () => {
    if (!window.confirm("Questa operazione cancellerà tutti i media locali e riscaricherà i dati dal server. Continuare?")) {
      return;
    }

    setIsSyncing(true);
    try {
      const result = await syncMedia();
      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm("Sei sicuro di voler eliminare definitivamente questa immagine?")) return;

    setIsDeleting(mediaId);
    try {
      const result = await deleteMedia(mediaId);
      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleAddMediaClick = () => {
    setNewMedia({
      tabella: 'techyshop_prodotti',
      element_id: '',
      alt_text: '',
      ordinamento: 0,
      principale: 0,
      file: null,
      filePreview: null
    });
    setOpenAddModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setNewMedia(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewMedia(prev => ({
        ...prev,
        file: file,
        filePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleSaveMedia = async () => {
    if (!newMedia.file) {
      setToast({ open: true, message: 'Seleziona un file da caricare.', severity: 'warning' });
      return;
    }
    if (!newMedia.element_id) {
      setToast({ open: true, message: 'Inserisci l\'ID dell\'elemento.', severity: 'warning' });
      return;
    }

    setIsUploading(true);
    try {
      const { file, ...metadata } = newMedia;
      const result = await addMedia(file, metadata);
      
      if (result.success) {
        setToast({ open: true, message: 'Media aggiunto con successo!', severity: 'success' });
        setOpenAddModal(false);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs & Navigation */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs
          separator={<ChevronRight fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/techyshop')}
          >
            TechyShop
          </Link>
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>Gestione Media</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddMediaClick}
          >
            Aggiungi Media
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncMedia}
            disabled={isSyncing}
          >
            {isSyncing ? 'Sincronizzazione...' : 'Sincronizza'}
          </Button>
          <IconButton color="primary" onClick={() => navigate('/techyshop')} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
            <MediaIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Header Section */}
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
        Gestione <Box component="span" sx={{ color: 'primary.main' }}>Media</Box>
      </Typography>

      {!mediaItems ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="primary" />
          <Typography sx={{ ml: 2, color: 'rgba(255,255,255,0.7)' }}>Caricamento media...</Typography>
        </Box>
      ) : mediaItems.length === 0 ? (
        <Paper sx={{ p: 4, mt: 4, backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', textAlign: 'center' }}>
          <MediaIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
            Nessun media trovato
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Non sono presenti media salvati nel database locale. Clicca su "Sincronizza" per scaricarli dal server.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mt: 4, overflowX: 'hidden' }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>
            Tutti i Media ({mediaItems.length})
          </Typography>
          <ImageList cols={4} gap={16} sx={{ 
            p: 0.5, // Spazio per l'effetto scale
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr) !important',
              sm: 'repeat(2, 1fr) !important',
              md: 'repeat(3, 1fr) !important',
              lg: 'repeat(4, 1fr) !important',
            }
          }}>
            {mediaItems.map((item) => (
              <ImageListItem key={item.id} sx={{ 
                borderRadius: 2, 
                overflow: 'hidden',
                backgroundColor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  borderColor: 'primary.main',
                }
              }}>
                <img
                  src={item.url ? (item.url.startsWith('http') ? item.url : `https://techyshop.techysumo.it${item.url}`) : ''}
                  alt={item.alt_text || 'Media'}
                  loading="lazy"
                  style={{ height: '240px', objectFit: 'contain', backgroundColor: '#000' }}
                />
                <ImageListItemBar
                  title={item.alt_text || `Media ID: ${item.id}`}
                  subtitle={
                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                        ID Media: {item.id}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        Collegato a: {item.tabella} (ID: {item.element_id})
                      </Typography>
                    </Box>
                  }
                  actionIcon={
                    <Tooltip title="Elimina Media">
                      <IconButton
                        sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'error.main' }, mr: 1 }}
                        onClick={() => handleDeleteMedia(item.id)}
                        disabled={isDeleting === item.id}
                      >
                        {isDeleting === item.id ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 70%, rgba(0,0,0,0) 100%)',
                    height: 'auto',
                    py: 1
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}

      {/* Add Media Modal */}
      <Dialog open={openAddModal} onClose={() => !isUploading && setOpenAddModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aggiungi Nuovo Media</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  border: '2px dashed rgba(255,255,255,0.2)', 
                  borderRadius: 2, 
                  p: 3, 
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' }
                }}
                onClick={() => document.getElementById('media-upload-input').click()}
              >
                <input
                  type="file"
                  id="media-upload-input"
                  hidden
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {newMedia.filePreview ? (
                  <img src={newMedia.filePreview} alt="Preview" style={{ maxHeight: '200px', maxWidth: '100%', borderRadius: '4px' }} />
                ) : (
                  <>
                    <UploadIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.5)', mb: 1 }} />
                    <Typography>Clicca per selezionare un'immagine</Typography>
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tabella"
                name="tabella"
                value={newMedia.tabella}
                onChange={handleInputChange}
                helperText="Es: techyshop_prodotti, techyshop_banner"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Element ID"
                name="element_id"
                type="number"
                value={newMedia.element_id}
                onChange={handleInputChange}
                helperText="ID del prodotto o dell'elemento a cui collegare l'immagine"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Testo Alt (Alt Text)"
                name="alt_text"
                value={newMedia.alt_text}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Ordinamento"
                name="ordinamento"
                type="number"
                value={newMedia.ordinamento}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox 
                    name="principale" 
                    checked={newMedia.principale === 1} 
                    onChange={handleInputChange} 
                  />
                }
                label="Immagine Principale"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} disabled={isUploading}>Annulla</Button>
          <Button 
            onClick={handleSaveMedia} 
            variant="contained" 
            disabled={isUploading}
            startIcon={isUploading && <CircularProgress size={20} color="inherit" />}
          >
            {isUploading ? 'Salvataggio...' : 'Salva Media'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={6000} 
        onClose={handleToastClose} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TechyShopMedia;
